<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApplicationsStore } from '@/stores/applications'

const route = useRoute()
const router = useRouter()
const applicationsStore = useApplicationsStore()

// 获取应用领域数据
const app = computed(() => applicationsStore.currentApplication)
const loading = computed(() => applicationsStore.loading)

// 在组件加载时获取应用领域详情
onMounted(async () => {
  const result = await applicationsStore.fetchApplicationById(route.params.id)
  if (!result) {
    router.push('/applications')
  }
})

// 返回列表
const goBack = () => {
  router.go(-1)
}
</script>

<template>
  <!-- 加载中状态 -->
  <div v-if="loading" class="app-detail-page loading-state">
    <div class="spinner"></div>
    <p>加载中...</p>
  </div>

  <!-- 数据来源提示 + 应用领域详情 -->
  <div v-else-if="app" class="app-detail-page">
    <!-- 数据来源提示 -->
    <div v-if="applicationsStore.usingMockData" class="data-source-notice">
      <p>ℹ️ 当前使用模拟数据（API 不可用）</p>
    </div>

    <div class="container">
      <!-- Breadcrumb -->
      <nav class="breadcrumb">
        <router-link to="/">首页</router-link>
        <span> / </span>
        <router-link to="/applications">应用领域</router-link>
        <span> / </span>
        <span>{{ app.name }}</span>
      </nav>

      <!-- Header Section -->
      <div class="detail-header">
        <div class="header-content">
          <div class="icon-large">{{ app.icon }}</div>
          <h1 class="title">{{ app.title }}</h1>
          <p class="subtitle">{{ app.description }}</p>
        </div>
        <div class="header-image">
          <img :src="app.image" :alt="app.name" />
        </div>
      </div>

      <!-- Details Section -->
      <div class="details-section">
        <!-- Description -->
        <div class="section">
          <h2 class="section-title">概述</h2>
          <p class="section-text">{{ app.details }}</p>
        </div>

        <!-- Features -->
        <div class="section">
          <h2 class="section-title">主要特性</h2>
          <div class="features-grid">
            <div v-for="(feature, idx) in app.features" :key="idx" class="feature-item">
              <div class="feature-number">{{ idx + 1 }}</div>
              <div class="feature-name">{{ feature }}</div>
            </div>
          </div>
        </div>

        <!-- Applications -->
        <div class="section">
          <h2 class="section-title">应用场景</h2>
          <div class="applications-list">
            <div v-for="(application, idx) in app.applications" :key="idx" class="app-item">
              <span class="app-icon">✓</span>
              <span class="app-text">{{ application }}</span>
            </div>
          </div>
        </div>

        <!-- Related Products -->
        <div class="section">
          <h2 class="section-title">相关产品</h2>
          <div class="products-list">
            <router-link
              v-for="productId in app.products"
              :key="productId"
              :to="`/products/${productId}`"
              class="product-link"
            >
              {{ productId }}
            </router-link>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="cta-section">
        <h2>需要更多信息？</h2>
        <p>我们的技术团队随时准备为您解答问题</p>
        <div class="cta-buttons">
          <button class="btn-primary" @click="goBack">返回列表</button>
          <router-link to="/about-us" class="btn-secondary">联系我们</router-link>
        </div>
      </div>
    </div>
  </div>

  <!-- Not Found -->
  <div v-else class="not-found">
    <div class="container">
      <h2>应用领域不存在</h2>
      <p>抱歉，您访问的应用领域不存在。</p>
      <router-link to="/applications" class="btn-back">返回应用领域列表</router-link>
    </div>
  </div>
</template>

<style scoped>
.data-source-notice {
  background: #e3f2fd;
  border-left: 4px solid #005eb8;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.data-source-notice p {
  margin: 0;
  color: #005eb8;
  font-size: 13px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 140px);
  text-align: center;
}

.loading-state .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #005eb8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.app-detail-page {
  background-color: #f9f9f9;
  padding: 40px 0 80px;
  min-height: calc(100vh - 140px);
}

.not-found {
  background-color: #f9f9f9;
  padding: 80px 0;
  text-align: center;
  min-height: calc(100vh - 140px);
}

.not-found h2 {
  font-size: 28px;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.not-found p {
  color: #666;
  margin-bottom: 24px;
}

.btn-back {
  display: inline-block;
  padding: 12px 32px;
  background-color: #005eb8;
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
}

.btn-back:hover {
  background-color: #004a94;
}

/* Breadcrumb */
.breadcrumb {
  margin-bottom: 32px;
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.breadcrumb a {
  color: #005eb8;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* Header Section */
.detail-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 60px;
  background: white;
  border-radius: 12px;
  padding: 60px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.icon-large {
  font-size: 80px;
  margin-bottom: 24px;
  line-height: 1;
}

.title {
  font-size: 40px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 16px 0;
  line-height: 1.3;
}

.subtitle {
  font-size: 18px;
  color: #666;
  margin: 0;
  line-height: 1.6;
}

.header-image {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  background: #e0e0e0;
}

.header-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Details Section */
.details-section {
  background: white;
  border-radius: 12px;
  padding: 60px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 40px;
}

.section {
  margin-bottom: 48px;
}

.section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 24px 0;
  padding-bottom: 16px;
  border-bottom: 2px solid #005eb8;
}

.section-text {
  font-size: 16px;
  color: #555;
  line-height: 1.8;
  margin: 0;
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.feature-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #005eb8;
}

.feature-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #005eb8;
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
}

.feature-name {
  font-size: 15px;
  color: #333;
  font-weight: 600;
}

/* Applications List */
.applications-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.app-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f0f8ff;
  border-radius: 6px;
  border-left: 3px solid #005eb8;
}

.app-icon {
  color: #005eb8;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
}

.app-text {
  color: #333;
  font-size: 15px;
  font-weight: 500;
}

/* Products List */
.products-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.product-link {
  display: inline-block;
  padding: 10px 20px;
  background: linear-gradient(135deg, #005eb8 0%, #003d7a 100%);
  color: white;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.product-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 94, 184, 0.3);
}

/* CTA Section */
.cta-section {
  background: linear-gradient(135deg, #005eb8 0%, #003d7a 100%);
  border-radius: 12px;
  padding: 60px;
  text-align: center;
  color: white;
}

.cta-section h2 {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.cta-section p {
  font-size: 16px;
  margin: 0 0 32px 0;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.btn-primary,
.btn-secondary {
  padding: 12px 32px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: white;
  color: #005eb8;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid white;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Responsive */
@media (max-width: 1024px) {
  .detail-header {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 40px;
  }

  .header-image {
    height: 300px;
  }

  .details-section {
    padding: 40px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .applications-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .detail-header {
    padding: 30px 20px;
  }

  .title {
    font-size: 28px;
  }

  .details-section {
    padding: 30px 20px;
  }

  .section-title {
    font-size: 20px;
  }

  .cta-section {
    padding: 40px 20px;
  }

  .cta-buttons {
    flex-direction: column;
  }
}
</style>
